import {
  DEFAULT_ZONE_ID,
  getDefaultZone,
  getZoneById,
  inferZoneIdFromLocation,
  isZoneId,
  type Zone,
  type ZoneId,
} from "@shared/zones";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

const ZONE_STORAGE_KEY = "ozk_local_zone";

interface ZoneContextType {
  selectedZoneId: ZoneId;
  selectedZone: Zone;
  setSelectedZoneId: (zoneId: ZoneId) => void;
  inferZoneFromLocation: (input: {
    zoneId?: string | null;
    suburb?: string | null;
    city?: string | null;
    state?: string | null;
  }) => ZoneId;
  isLoading: boolean;
}

const ZoneContext = createContext<ZoneContextType | undefined>(undefined);

export function ZoneProvider({ children }: { children: ReactNode }) {
  const [selectedZoneId, setSelectedZoneIdState] =
    useState<ZoneId>(DEFAULT_ZONE_ID);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      const storedZoneId = localStorage.getItem(ZONE_STORAGE_KEY);
      if (isZoneId(storedZoneId)) {
        setSelectedZoneIdState(storedZoneId);
      }
    } catch (error) {
      console.warn("[ZoneContext] Failed to load selected zone", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const setSelectedZoneId = useCallback((zoneId: ZoneId) => {
    setSelectedZoneIdState(zoneId);
    localStorage.setItem(ZONE_STORAGE_KEY, zoneId);
  }, []);

  const selectedZone = useMemo(
    () => getZoneById(selectedZoneId) ?? getDefaultZone(),
    [selectedZoneId],
  );

  const value = useMemo<ZoneContextType>(
    () => ({
      selectedZoneId,
      selectedZone,
      setSelectedZoneId,
      inferZoneFromLocation: inferZoneIdFromLocation,
      isLoading,
    }),
    [selectedZoneId, selectedZone, setSelectedZoneId, isLoading],
  );

  return <ZoneContext.Provider value={value}>{children}</ZoneContext.Provider>;
}

export function useZone() {
  const context = useContext(ZoneContext);
  if (!context) {
    throw new Error("useZone must be used within a ZoneProvider");
  }
  return context;
}

